import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, HStack, VStack, Button, Spinner,
  Badge, Icon, Table, Thead, Tbody, Tr, Th, Td,
  Alert, AlertIcon, useToast, IconButton, Tooltip,
  NumberInput, NumberInputField, NumberInputStepper,
  NumberIncrementStepper, NumberDecrementStepper,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { FiRefreshCw, FiMaximize2, FiRotateCw } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

function Deployments() {
  const [deployments, setDeployments] = useState([]);
  const [namespace, setNamespace] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scaleTarget, setScaleTarget] = useState(null);
  const [scaleReplicas, setScaleReplicas] = useState(1);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchDeployments = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/k8s/deployments/`, { headers: getAuthHeaders() })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        setDeployments(data.deployments || []);
        setNamespace(data.namespace || '');
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { fetchDeployments(); }, [fetchDeployments]);

  const openScaleModal = (dep) => {
    setScaleTarget(dep.name);
    setScaleReplicas(dep.replicas);
    onOpen();
  };

  const doScale = async () => {
    try {
      const res = await fetch(`${API_BASE}/k8s/deployments/${scaleTarget}/scale/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ replicas: scaleReplicas }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: `Scaled ${scaleTarget} to ${scaleReplicas} replicas`, status: 'success', duration: 3000 });
      onClose();
      fetchDeployments();
    } catch (err) {
      toast({ title: 'Failed to scale', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const restartDeployment = async (name) => {
    if (!window.confirm(`Restart deployment "${name}"?`)) return;
    try {
      const res = await fetch(`${API_BASE}/k8s/deployments/${name}/restart/`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast({ title: `Restarted ${name}`, status: 'success', duration: 3000 });
      fetchDeployments();
    } catch (err) {
      toast({ title: 'Failed to restart', description: err.message, status: 'error', duration: 5000 });
    }
  };

  if (loading) return <Box textAlign="center" py={20}><Spinner size="xl" color="gray.500" /></Box>;
  if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Deployments</Heading>
          <Text color="gray.500">
            {deployments.length} deployments in namespace <Badge colorScheme="blue">{namespace}</Badge>
          </Text>
        </VStack>
        <Button leftIcon={<FiRefreshCw />} colorScheme="gray" onClick={fetchDeployments}>Refresh</Button>
      </HStack>

      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        <Table variant="simple" size="sm">
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Replicas</Th>
              <Th>Ready</Th>
              <Th>Available</Th>
              <Th>Images</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {deployments.map((dep) => {
              const healthy = dep.ready_replicas >= dep.replicas && dep.replicas > 0;
              return (
                <Tr key={dep.name} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <Text fontWeight="medium" fontSize="sm">{dep.name}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={dep.replicas > 0 ? 'blue' : 'gray'}>{dep.replicas}</Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={healthy ? 'green' : 'orange'}>
                      {dep.ready_replicas} / {dep.replicas}
                    </Badge>
                  </Td>
                  <Td>{dep.available_replicas}</Td>
                  <Td>
                    {(dep.images || []).map((img, i) => (
                      <Text key={i} fontSize="xs" color="gray.500" isTruncated maxW="300px">{img}</Text>
                    ))}
                  </Td>
                  <Td>
                    <Text fontSize="xs" color="gray.500">
                      {dep.created_at ? new Date(dep.created_at).toLocaleDateString() : '—'}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Tooltip label="Scale">
                        <IconButton
                          icon={<FiMaximize2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => openScaleModal(dep)}
                          aria-label="Scale deployment"
                        />
                      </Tooltip>
                      <Tooltip label="Restart">
                        <IconButton
                          icon={<FiRotateCw />}
                          size="sm"
                          variant="ghost"
                          colorScheme="orange"
                          onClick={() => restartDeployment(dep.name)}
                          aria-label="Restart deployment"
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Scale Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Scale: {scaleTarget}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>Set the desired number of replicas:</Text>
            <NumberInput
              value={scaleReplicas}
              onChange={(_, val) => setScaleReplicas(val)}
              min={0}
              max={20}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={doScale}>Scale</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Deployments;
